import React, { useState, useEffect, useRef } from 'react';

// Album art thumbnail that never makes the layout jump:
// - keeps showing the previous artwork until the new one has fully loaded,
//   then cross-fades to it
// - holds the space with a placeholder tile while the first image loads,
//   or if an image fails to load
// - ignores brief gaps (Discord often sends a track change before its artwork),
//   only fading out if the artwork stays gone
const FADE_MS = 400;
const GRACE_MS = 4000;
const PLACEHOLDER = '__placeholder__';

function Layer({ value, fadeIn }) {
  const style = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    ...(fadeIn && { animation: `albumArtFadeIn ${FADE_MS}ms ease forwards` }),
  };
  if (value === PLACEHOLDER) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#18181b' }}>
        <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="#52525b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
        </svg>
      </div>
    );
  }
  return <img src={value} alt="" referrerPolicy="no-referrer" style={{ ...style, objectFit: 'cover', display: 'block' }} />;
}

export default function AlbumArt({ src, size, style }) {
  const [shown, setShown] = useState(src ? PLACEHOLDER : null);
  const [prev, setPrev] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const shownRef = useRef(shown);
  shownRef.current = shown;

  // Cross-fade: the old layer stays underneath while the new one fades in
  const show = (next) => {
    if (next === shownRef.current) return;
    setPrev(shownRef.current);
    setShown(next);
    shownRef.current = next;
  };

  // Drop the old layer once the fade has finished
  useEffect(() => {
    if (!prev) return;
    const timer = setTimeout(() => setPrev(null), FADE_MS + 50);
    return () => clearTimeout(timer);
  }, [prev, shown]);

  useEffect(() => {
    if (!src) {
      if (!shownRef.current) return;
      // Only remove the artwork if it stays gone, then fade out before collapsing
      const fadeTimer = setTimeout(() => setLeaving(true), GRACE_MS);
      const removeTimer = setTimeout(() => {
        setShown(null);
        setPrev(null);
        setLeaving(false);
      }, GRACE_MS + FADE_MS);
      return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
    }

    setLeaving(false);
    if (!shownRef.current) {
      setShown(PLACEHOLDER); // reserve the space straight away
      shownRef.current = PLACEHOLDER;
    }
    // Preload, and only swap once the new image is ready
    let cancelled = false;
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => { if (!cancelled) show(src); };
    img.onerror = () => { if (!cancelled) show(PLACEHOLDER); };
    img.src = src;
    return () => { cancelled = true; };
  }, [src]);

  if (!shown && !prev) return null;

  return (
    <div
      style={{
        position: 'relative',
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid rgba(63,63,70,0.5)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
        background: '#18181b',
        opacity: leaving ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        ...style,
      }}
    >
      <style>{`@keyframes albumArtFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      {prev && <Layer key={`prev-${prev}`} value={prev} />}
      {shown && <Layer key={`shown-${shown}`} value={shown} fadeIn={Boolean(prev)} />}
    </div>
  );
}
